/**
 * Calorie / TDEE calculator logic.
 * Internally metric (kg, cm). Mifflin-St Jeor default.
 */

export type Gender = "male" | "female";
export type Formula = "mifflin" | "harris" | "katch";
export type Activity = "sedentary" | "light" | "moderate" | "very" | "extra";
export type Goal = "maintain" | "mild_loss" | "moderate_loss" | "mild_gain" | "moderate_gain";

export const ACTIVITY_MULTIPLIER: Record<Activity, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  very: 1.725,
  extra: 1.9,
};

export const GOAL_DELTA: Record<Goal, number> = {
  maintain: 0,
  mild_loss: -250,
  moderate_loss: -500,
  mild_gain: 250,
  moderate_gain: 500,
};

export const SAFETY_FLOOR: Record<Gender, number> = {
  male: 1500,
  female: 1200,
};

export interface CalorieInput {
  gender: Gender;
  age: number;
  heightCm: number;
  weightKg: number;
  activity: Activity;
  goal: Goal;
  formula: Formula;
  bodyFatPct?: number; // for Katch-McArdle
}

export interface MacroSplit {
  carbsPct: number;
  proteinPct: number;
  fatPct: number;
}

export interface MacroGrams {
  carbsG: number;
  proteinG: number;
  fatG: number;
  carbsKcal: number;
  proteinKcal: number;
  fatKcal: number;
}

export interface CalorieResult {
  bmr: number;
  tdee: number;
  activityKcal: number;
  goalKcalRaw: number;
  goalKcal: number; // after safety floor
  flooredApplied: boolean;
  macros: MacroGrams;
  macroSplit: MacroSplit;
  waterMlBase: number;
  waterMlHotClimate: number;
  warnings: WarningKey[];
  bmi: number;
}

export type WarningKey =
  | "below_safety_floor"
  | "underweight_loss"
  | "obese_gain"
  | "adolescent"
  | "katch_missing_bf";

/* -------- BMR formulas -------- */

export function mifflinBMR(g: Gender, kg: number, cm: number, age: number): number {
  const base = 10 * kg + 6.25 * cm - 5 * age;
  return g === "male" ? base + 5 : base - 161;
}

export function harrisBMR(g: Gender, kg: number, cm: number, age: number): number {
  return g === "male"
    ? 88.362 + 13.397 * kg + 4.799 * cm - 5.677 * age
    : 447.593 + 9.247 * kg + 3.098 * cm - 4.330 * age;
}

export function katchBMR(kg: number, bodyFatPct: number): number {
  const lbm = kg * (1 - bodyFatPct / 100);
  return 370 + 21.6 * lbm;
}

/* -------- Macro defaults -------- */

export function defaultMacroSplit(goal: Goal): MacroSplit {
  if (goal === "mild_loss" || goal === "moderate_loss") {
    return { carbsPct: 40, proteinPct: 35, fatPct: 25 };
  }
  return { carbsPct: 50, proteinPct: 25, fatPct: 25 };
}

export function macrosFromKcal(kcal: number, split: MacroSplit): MacroGrams {
  const carbsKcal = (kcal * split.carbsPct) / 100;
  const proteinKcal = (kcal * split.proteinPct) / 100;
  const fatKcal = (kcal * split.fatPct) / 100;
  return {
    carbsKcal: Math.round(carbsKcal),
    proteinKcal: Math.round(proteinKcal),
    fatKcal: Math.round(fatKcal),
    carbsG: Math.round(carbsKcal / 4),
    proteinG: Math.round(proteinKcal / 4),
    fatG: Math.round(fatKcal / 9),
  };
}

/* -------- Validation -------- */

export type CalorieError =
  | "age_invalid"
  | "height_invalid"
  | "weight_invalid"
  | "bf_invalid"
  | "missing";

export function validate(inp: Partial<CalorieInput>):
  | { ok: true }
  | { ok: false; error: CalorieError } {
  const { age, heightCm, weightKg, formula, bodyFatPct } = inp;
  if (age == null || heightCm == null || weightKg == null) return { ok: false, error: "missing" };
  if (!Number.isFinite(age) || age < 15 || age > 100) return { ok: false, error: "age_invalid" };
  if (!Number.isFinite(heightCm) || heightCm < 80 || heightCm > 260) return { ok: false, error: "height_invalid" };
  if (!Number.isFinite(weightKg) || weightKg < 25 || weightKg > 350) return { ok: false, error: "weight_invalid" };
  if (formula === "katch") {
    if (bodyFatPct == null || !Number.isFinite(bodyFatPct) || bodyFatPct < 3 || bodyFatPct > 60) {
      return { ok: false, error: "bf_invalid" };
    }
  }
  return { ok: true };
}

/* -------- Main compute -------- */

export function computeCalories(input: CalorieInput): CalorieResult {
  const { gender, age, heightCm, weightKg, activity, goal, formula, bodyFatPct } = input;

  let bmr: number;
  if (formula === "harris") bmr = harrisBMR(gender, weightKg, heightCm, age);
  else if (formula === "katch") bmr = katchBMR(weightKg, bodyFatPct ?? 20);
  else bmr = mifflinBMR(gender, weightKg, heightCm, age);

  const mult = ACTIVITY_MULTIPLIER[activity];
  const tdee = bmr * mult;
  const activityKcal = tdee - bmr;

  const goalKcalRaw = tdee + GOAL_DELTA[goal];
  const floor = SAFETY_FLOOR[gender];
  const flooredApplied = goalKcalRaw < floor;
  const goalKcal = flooredApplied ? floor : goalKcalRaw;

  const macroSplit = defaultMacroSplit(goal);
  const macros = macrosFromKcal(goalKcal, macroSplit);

  const waterMlBase = Math.round(35 * weightKg);
  const waterMlHotClimate = waterMlBase + 500;

  const heightM = heightCm / 100;
  const bmi = weightKg / (heightM * heightM);

  const warnings: WarningKey[] = [];
  if (flooredApplied) warnings.push("below_safety_floor");
  if (bmi < 18.5 && (goal === "mild_loss" || goal === "moderate_loss")) warnings.push("underweight_loss");
  if (bmi > 35 && (goal === "mild_gain" || goal === "moderate_gain")) warnings.push("obese_gain");
  if (age < 18) warnings.push("adolescent");

  return {
    bmr: Math.round(bmr),
    tdee: Math.round(tdee),
    activityKcal: Math.round(activityKcal),
    goalKcalRaw: Math.round(goalKcalRaw),
    goalKcal: Math.round(goalKcal),
    flooredApplied,
    macros,
    macroSplit,
    waterMlBase,
    waterMlHotClimate,
    warnings,
    bmi: +bmi.toFixed(1),
  };
}
